import { useCallback, useEffect, useRef, useState } from "react";

import { rewriteText } from "services/documentRewriterService";

// Short pastes (an MRN, a drug name, a corrected sentence) aren't worth an LLM
// round-trip and rarely contain the patterns we're normalizing.
const MIN_REWRITE_CHARS = 200;

// The longest the step waits on a clarify. Past this the paste is used as it is and a late
// result is dropped, so a slow rewrite can never hold the encounter for minutes.
const MAX_WAIT_MS = 20000;

/**
 * Runs pasted transcript text through the document rewriter in the background (it makes
 * ambiguous negations easier for the AI steps to read) and swaps the clarified text in when it
 * returns. Silent by design: nothing to review, and a failure just leaves the paste as it was.
 *
 * Only the pasted segment is rewritten — text the clinician already typed is never sent or
 * touched. The raw paste lands first so the field is never blocked. `rewriting` is exposed so
 * the step's Next can wait for the clarified text rather than run on the raw paste.
 *
 * @param {{
 *   transcript: string,
 *   onTranscriptChange: (value: string) => void,
 *   enabled?: boolean,
 *   disabled?: boolean,
 * }} params
 */
export default function useTranscriptRewrite({
  transcript,
  onTranscriptChange,
  enabled = true,
  disabled = false,
}) {
  const [rewriting, setRewriting] = useState(false);

  // The rewrite resolves after the paste has been applied, so handlers can't
  // close over `transcript` — read the live value from a ref.
  const transcriptRef = useRef(transcript);
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Guards against a stale in-flight rewrite clobbering a newer paste.
  const requestIdRef = useRef(0);

  const handlePaste = useCallback(
    (event) => {
      if (disabled || !enabled) {
        return;
      }

      const pasted = event.clipboardData?.getData("text") ?? "";
      if (pasted.trim().length < MIN_REWRITE_CHARS) {
        // Let the browser paste it normally.
        return;
      }

      // We own the insertion from here, so the exact pasted segment is known
      // and can be swapped for its rewritten form later.
      event.preventDefault();

      const target = event.target;
      const current = transcriptRef.current || "";
      const start = target.selectionStart ?? current.length;
      const end = target.selectionEnd ?? current.length;
      const rawValue = current.slice(0, start) + pasted + current.slice(end);

      // Show the raw paste immediately — the rewrite is an enhancement, not a
      // gate on the clinician seeing their own text.
      transcriptRef.current = rawValue;
      onTranscriptChange?.(rawValue);

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setRewriting(true);

      const giveUp = setTimeout(() => {
        if (requestIdRef.current !== requestId) return;
        requestIdRef.current += 1;
        setRewriting(false);
      }, MAX_WAIT_MS);

      rewriteText(pasted)
        .then(({ rewrittenText }) => {
          if (requestIdRef.current !== requestId || !rewrittenText) return;
          const latest = transcriptRef.current || "";
          // Edited over while it was being clarified: leave their edit alone.
          if (!latest.includes(pasted)) return;
          // Function replacement: a literal `$&` / `$1` in clinical text must not
          // be interpreted as a replacement pattern.
          const nextValue = latest.replace(pasted, () => rewrittenText);
          transcriptRef.current = nextValue;
          onTranscriptChange?.(nextValue);
        })
        .catch((error) => {
          // Non-fatal by design — the raw paste is already in the field.
          console.error("[useTranscriptRewrite] Rewrite failed:", error);
        })
        .finally(() => {
          clearTimeout(giveUp);
          if (requestIdRef.current === requestId) setRewriting(false);
        });
    },
    [disabled, enabled, onTranscriptChange],
  );

  return { rewriting, handlePaste, MIN_REWRITE_CHARS };
}
